import {
  OutboundEmail_Read,
  OutboundEmailDraft_Attachment,
  OutboundEmailDraft_Read,
  OutboundEmailDraft_RecipientParty,
  OutboundEmailRecipient_Read,
  OutboundEmailRecipientState,
  OutboundEmailState,
  Principal_Read_Nested,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

class OutboundEmailRecipient {
  constructor(
    private _id: string,
    private _email: string,
    private _state: OutboundEmailRecipientState,
  ) {}

  public static parse(proto: OutboundEmailRecipient_Read) {
    return new OutboundEmailRecipient(proto.id, proto.email, proto.state);
  }

  public get id() {
    return this._id;
  }

  public get email() {
    return this._email;
  }

  public get state() {
    return this._state;
  }
}

export class OutboundEmail {
  constructor(
    private _id: string,
    private _operation_id: string,
    private _state: OutboundEmailState,
    private _subject: string,
    private _message: string | null,
    private _sent_by: Principal_Read_Nested | null,
    private _recipients: OutboundEmailRecipient[],
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
  ) {}

  public static parse(proto: OutboundEmail_Read) {
    return new OutboundEmail(
      proto.id,
      proto.operation_id,
      proto.state,
      proto.subject,
      proto.message ?? null,
      proto.sent_by ?? null,
      proto.recipients.map(OutboundEmailRecipient.parse),
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
    );
  }

  public get id() {
    return this._id;
  }

  public get operationId() {
    return this._operation_id;
  }

  public get state() {
    return this._state;
  }

  public get subject() {
    return this._subject;
  }

  public get message() {
    return this._message;
  }

  public get sentBy() {
    return this._sent_by;
  }

  public get recipients() {
    return this._recipients;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }
}

type OutboundEmailDraftWithAttachments_Read = OutboundEmailDraft_Read & {
  attachments: OutboundEmailDraft_Attachment[];
};

class OutboundEmailDraftRecipientParty {
  constructor(private _name: string) {}

  public static parse(proto: OutboundEmailDraft_RecipientParty) {
    return new OutboundEmailDraftRecipientParty(proto.name);
  }

  public get name() {
    return this._name;
  }
}

class OutboundEmailDraftAttachment {
  constructor(private _file_name: string) {}

  public static parse(proto: OutboundEmailDraft_Attachment) {
    return new OutboundEmailDraftAttachment(proto.file_name);
  }

  public get fileName() {
    return this._file_name;
  }
}

export interface OutboundEmailDraftFields {
  attachments: OutboundEmailDraftAttachment[];
  message: string;
  recipientParty: OutboundEmailDraftRecipientParty | null;
  recipients: string[];
  subject: string;
}

export abstract class OutboundEmailDraft {
  constructor(private _draft: OutboundEmailDraftFields) {}

  protected static parseFields(proto: OutboundEmailDraftWithAttachments_Read) {
    return {
      attachments: proto.attachments.map(OutboundEmailDraftAttachment.parse),
      message: proto.message,
      recipientParty: proto.recipient_party
        ? OutboundEmailDraftRecipientParty.parse(proto.recipient_party)
        : null,
      recipients: proto.recipients.map(({ email }) => email),
      subject: proto.subject,
    };
  }

  public abstract get ids(): string[];

  public get recipients() {
    return this._draft.recipients;
  }

  public get subject() {
    return this._draft.subject;
  }

  public get message() {
    return this._draft.message;
  }

  public get recipientParty() {
    return this._draft.recipientParty;
  }

  public get attachments() {
    return this._draft.attachments;
  }
}
